// apps/web/src/pages/auth/SignIn.tsx
//
// Plan 04-12b Task 1 — sign-in form.
//
// FIELDS (CONTEXT D-21):
//   - email
//   - password
//   - Sign in button
//   - Sign in with Google / GitHub buttons (calls authClient.signIn.social)
//   - Forgot password link → /auth/forgot-password
//
// FLOW:
//   1. User submits email+password → authClient.signIn.email
//   2. Response carries either:
//        - {data: {user, session}} → redirect to /
//        - {data: {twoFactorRedirect: true}} (Better-Auth twoFactor plugin's
//          response when user has MFA enrolled) → redirect to /auth/mfa-challenge
//        - {error: {code: 'INVALID_EMAIL_OR_PASSWORD', ...}} → show GENERIC
//          "Invalid credentials" — T8 enumeration defense
//
// T8 DEFENSE (T-04-04-01 enumeration uniformity):
//   The server returns a uniform error code (INVALID_EMAIL_OR_PASSWORD) for
//   BOTH unknown-email AND wrong-password cases. The client MUST present a
//   generic "Invalid credentials" message — NEVER expose which arm of the
//   credential failed.
//
// OAUTH BUTTONS:
//   authClient.signIn.social({provider: 'google'|'github'}) triggers the
//   OAuth round-trip. Better-Auth issues a state token + redirects to the
//   provider. The provider redirects back to /api/auth/callback/<provider>;
//   the AUTH-12 hook (Plan 04-07) fires there. // sanitize-allow: AUTH-12

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { EmailField, PasswordField } from "@/components/forms/EmailPasswordFields";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { authClient } from "@/lib/auth-client";
import { computeAllowedReturnTo } from "@/lib/safe-return-to";

const signInSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email"),
  password: z.string().min(1, "Password is required"),
});

type SignInFormValues = z.infer<typeof signInSchema>;

export function SignIn(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Plan 08-11 Task 2 (W5): read raw ?returnTo= and resolve through the shared
  // helper. The helper bounds navigation to internal allow-listed paths only —
  // any off-list value (open-redirect attempts, absolute URLs, protocol-
  // relative URLs) silently falls back to "/". DO NOT inline the regex here;
  // the single source of truth lives in @/lib/safe-return-to so Plan 08-10's
  // RecipeEditor 401 fallback can import the SAME helper.
  const returnToRaw = searchParams.get("returnTo");
  const [serverError, setServerError] = useState<string | null>(null);
  const [oauthPending, setOauthPending] = useState<"google" | "github" | null>(null);

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onSubmit",
  });

  async function onSubmit(values: SignInFormValues): Promise<void> {
    setServerError(null);
    try {
      const result = (await authClient.signIn.email({
        email: values.email,
        password: values.password,
      })) as {
        data: {
          user?: { id: string };
          session?: { id: string };
          twoFactorRedirect?: boolean;
        } | null;
        error: { message?: string; code?: string } | null;
      };
      if (result.error !== null && result.error !== undefined) {
        // T8 defense — uniform generic message regardless of error code.
        // The server already returns a uniform error code; we further harden
        // by showing the same client-side text for any auth failure.
        setServerError("Invalid credentials");
        return;
      }
      // Better-Auth's twoFactor plugin returns { twoFactorRedirect: true,
      // twoFactorMethods: [...] } when the user has MFA enrolled. Plan 04-08
      // verifies this is the library default.
      if (
        result.data !== null &&
        result.data !== undefined &&
        result.data.twoFactorRedirect === true
      ) {
        navigate("/auth/mfa-challenge");
        return;
      }
      navigate(computeAllowedReturnTo(returnToRaw));
    } catch {
      setServerError("Invalid credentials");
    }
  }

  async function onOAuth(provider: "google" | "github"): Promise<void> {
    setServerError(null);
    setOauthPending(provider);
    try {
      // authClient.signIn.social triggers a full-page redirect to the OAuth
      // provider's authorization endpoint. The callback (handled by the API
      // catch-all at /api/auth/callback/<provider>) issues the cookie + the
      // AUTH-12 link-existing redirect when applicable. // sanitize-allow: AUTH-12
      await authClient.signIn.social({ provider });
    } catch {
      setServerError("Could not start the social sign-in flow; please try again");
    } finally {
      setOauthPending(null);
    }
  }

  const isPending = form.formState.isSubmitting || oauthPending !== null;

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>Welcome back. Sign in to continue.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {serverError !== null ? (
            <Alert variant="destructive">
              <AlertTitle>Sign-in failed</AlertTitle>
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          ) : null}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <EmailField
                control={form.control}
                name="email"
                autoComplete="email"
                autoFocus
                disabled={isPending}
              />
              <PasswordField
                control={form.control}
                name="password"
                label="Password"
                autoComplete="current-password"
                disabled={isPending}
              />
              <div className="flex justify-end">
                <Link to="/auth/forgot-password" className="text-sm font-medium underline">
                  Forgot password?
                </Link>
              </div>
              <Button type="submit" className="w-full" disabled={isPending}>
                {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </Form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void onOAuth("google");
              }}
              disabled={isPending}
            >
              {oauthPending === "google" ? "Redirecting…" : "Sign in with Google"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void onOAuth("github");
              }}
              disabled={isPending}
            >
              {oauthPending === "github" ? "Redirecting…" : "Sign in with GitHub"}
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/auth/sign-up" className="font-medium underline">
              Create an account
            </Link>
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
